import { useEffect, useRef, useState } from "react";
import ReactCardFlip from "react-card-flip";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import InfoIcon from "@mui/icons-material/Info";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionIcon from "@mui/icons-material/Description";

export default function FlipCard({ shouldFlip, onFlipped, name, imageUrl, biography, cv, roles = [] }) {
  const theme = useTheme();
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [isFlipped, setIsFlipped] = useState(false);
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const focusOnChange = useRef(false);

  const changeFace = (value) => {
    focusOnChange.current = true;
    setIsFlipped(value);
  };

  useEffect(() => {
    if (shouldFlip) {
      focusOnChange.current = true;
      setIsFlipped(true);
      if (onFlipped) onFlipped();
    }
  }, [shouldFlip, onFlipped]);

  useEffect(() => {
    if (focusOnChange.current) {
      (isFlipped ? backRef : frontRef).current?.focus({ preventScroll: true });
      focusOnChange.current = false;
    }
  }, [isFlipped]);

  const paperStyle = {
    width: "100%", height: "100%", borderRadius: 2, overflow: "hidden",
    border: "1px solid rgba(6,57,112,0.1)", boxShadow: "0 18px 44px rgba(6,57,112,0.12)",
    backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden",
  };

  return (
    <Box sx={{ width: "min(100%, 340px)", height: 470, mx: "auto", perspective: "1200px", "& > div, & > div > div": { width: "100% !important", height: "100% !important" } }}>
      <ReactCardFlip isFlipped={isFlipped} flipDirection="horizontal"
        flipSpeedFrontToBack={reduceMotion ? 0 : 0.6} flipSpeedBackToFront={reduceMotion ? 0 : 0.6}
        containerStyle={{ width: "100%", height: "100%" }}>
        <Paper aria-hidden={isFlipped} inert={isFlipped ? "" : undefined}
          onClick={() => changeFace(true)} elevation={0} sx={{ ...paperStyle, cursor: "pointer" }}>
          <Box sx={{ position: "relative", height: "100%" }}>
            <Box component="img" src={imageUrl} alt={name} loading="lazy" decoding="async"
              sx={{ width: "100%", height: 330, objectFit: "cover", filter: "saturate(0.95)" }} />
            <Box sx={{ p: 2, height: 140, display: "flex", flexDirection: "column", justifyContent: "center", pr: 7 }}>
              <Typography component="h3" variant="h5" sx={{ color: "primary.main", fontFamily: "Georgia, serif", fontWeight: 700 }}>{name}</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{roles.join(" · ")}</Typography>
            </Box>
            <IconButton ref={frontRef} tabIndex={isFlipped ? -1 : 0} aria-label={`Read more about ${name}`}
              sx={{ position: "absolute", right: 12, bottom: 12, bgcolor: "primary.main", color: "white", "&:hover": { bgcolor: "primary.dark" } }}>
              <InfoIcon />
            </IconButton>
          </Box>
        </Paper>
        <Paper aria-hidden={!isFlipped} inert={!isFlipped ? "" : undefined} elevation={0} sx={paperStyle}>
          <Box role="region" aria-label={`${name} biography`} tabIndex={isFlipped ? 0 : -1}
            onClick={() => changeFace(false)}
            sx={{ p: 2.25, height: 386, overflowY: "auto", borderTop: `5px solid ${theme.palette.primary.main}` }}>
            <Typography component="h3" variant="h5" sx={{ color: "primary.main", fontFamily: "Georgia, serif", fontWeight: 700, mb: 1.5 }}>{name}</Typography>
            <Typography sx={{ whiteSpace: "pre-line", color: "text.secondary", lineHeight: 1.7 }}>{biography || "Biography coming soon."}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: 84, px: 1.5, borderTop: "1px solid rgba(6,57,112,0.1)" }}>
            <IconButton ref={backRef} tabIndex={isFlipped ? 0 : -1} onClick={() => changeFace(false)} aria-label={`Back to ${name}'s portrait`}>
              <ArrowBackIcon sx={{ color: "primary.main" }} />
            </IconButton>
            {cv && <Button component="a" href={cv} tabIndex={isFlipped ? 0 : -1} target="_blank" rel="noopener noreferrer" startIcon={<DescriptionIcon />} aria-label={`${name}'s CV (opens in a new tab)`}>CV</Button>}
          </Box>
        </Paper>
      </ReactCardFlip>
    </Box>
  );
}
